import { createEffect, sample } from 'effector';
import { t } from 'i18n';
import $$api from 'models/api';
import { $isDocsGenerationPage, $queryParams } from 'models/location';
import $$docsGeneration from 'models/pages/docs-generation';
import { $qInstances } from 'models/q-instances';
import { navigateFx } from 'models/router-gate';
import { z } from 'zod';

import AlertsService from 'services/AlertsService';
import DocumentDownloadService from 'services/DocumentDownloadService';
import createPollingFx from 'utils/factories/create-polling-fx';

import { Document } from 'types/zod/document';

$$docsGeneration.$qInstance.reset($isDocsGenerationPage.map((isDocsGenerationPage) => !isDocsGenerationPage));
$$docsGeneration.$jobId.reset($isDocsGenerationPage.map((isDocsGenerationPage) => !isDocsGenerationPage));
$$docsGeneration.$qInstanceId.reset($isDocsGenerationPage.map((isDocsGenerationPage) => !isDocsGenerationPage));

$$docsGeneration.pollingFx.use(
    createPollingFx({
        limit: 30,
        interval: 1000,
        fx: $$api.qInstances.getJobStatusFx,
        condition: (result) => {
            return !!result.find((doc) => ['In_Progress', 'Generated', 'Deleted'].includes(doc.document_status_code));
        },
    }),
);

// After docs generation page is opened, get qInstanceId from the query params, get qInstance from the $qInstances store and set it to the $qInstance store
sample({
    source: {
        isDocsGenerationPage: $isDocsGenerationPage,
        qInstances: $qInstances,
        qInstanceId: $queryParams.map((params) => +params.qInstanceId),
    },
    filter: ({ isDocsGenerationPage }) => isDocsGenerationPage,
    fn: ({ qInstances, qInstanceId }) => qInstances[qInstanceId] || null,
    target: $$docsGeneration.$qInstance,
});

// When "job" appears in the query params, we set it to the store
sample({
    source: $queryParams.map((params) => +params.job),
    clock: $isDocsGenerationPage,
    filter: (_, isDocsGenerationPage) => isDocsGenerationPage,
    target: $$docsGeneration.$jobId,
});

// When "qInstanceId" appears in the query params, we set it to the store
sample({
    source: $queryParams.map((params) => +params.qInstanceId),
    clock: $isDocsGenerationPage,
    filter: (_, isDocsGenerationPage) => isDocsGenerationPage,
    target: $$docsGeneration.$qInstanceId,
});

const isTrustFundDocExecuted = (documents: Document[]) => {
    return (
        documents.length === 1 &&
        documents[0].document_type_code === 'Trust_Fund' &&
        documents[0].document_status_code === 'Executed'
    );
};

// When trust fund document is executed, we download it
sample({
    clock: $$api.qInstances.getJobStatusFx.doneData.filter({ fn: isTrustFundDocExecuted }),
    fn: (documents) => documents[0].document_id,
    target: createEffect(DocumentDownloadService.downloadPdf),
});
// When trust fund document is executed, we show success alert
sample({
    clock: $$api.qInstances.getJobStatusFx.doneData.filter({ fn: isTrustFundDocExecuted }),
    fn: () => t('trustFunding.alerts.downloading'),
    target: createEffect(AlertsService.showSuccess),
});

// When "qInstanceId" appears in query params for generation waiting page, pull qInstance
sample({
    source: {
        id: $$docsGeneration.$qInstanceId,
        isDocsGenerationPage: $isDocsGenerationPage,
    },
    filter: ({ id, isDocsGenerationPage }) => isDocsGenerationPage && !!id,
    fn: ({ id }) => z.number().parse(id),
    target: $$api.qInstances.getQInstanceFx,
});

// When generation page is opened, take qInstanceId and job from the query params and start polling
sample({
    source: {
        id: $$docsGeneration.$qInstanceId,
        job: $$docsGeneration.$jobId,
        isDocsGenerationPage: $isDocsGenerationPage,
    },
    filter: ({ id, job, isDocsGenerationPage }) => isDocsGenerationPage && !!id && !!job,
    fn: (d) => z.object({ id: z.number(), job: z.number() }).parse(d),
    target: $$docsGeneration.pollingFx,
});

// When polling is done, we navigate to the next page
sample({
    source: $$docsGeneration.$qInstance,
    clock: $$docsGeneration.pollingFx.done,
    filter: (qInstance) => !!qInstance,
    fn: (qInstance) => {
        if (qInstance?.questionnaire_type_code === 'EP') {
            return '/dashboard/complete';
        }
        if (qInstance?.questionnaire_type_code === 'TRUST_FUND') {
            return '/dashboard/trust-funding';
        }
        if (qInstance?.questionnaire_type_code === 'GIFT_TRUST') {
            return '/dashboard/gift-trust';
        }
        return '/dashboard/complete';
    },
    target: navigateFx,
});

// When polling is done, pull community, qInstance and current participant
sample({
    clock: $$docsGeneration.pollingFx.done,
    fn: () => undefined,
    target: [$$api.participants.getCommunityFx, $$api.participants.getMeFx],
});
sample({
    source: $$docsGeneration.$qInstance,
    clock: $$docsGeneration.pollingFx.done,
    filter: (qInstance) => !!qInstance,
    fn: (qInstance) => z.number().parse(qInstance?.questionnaire_instance_id),
    target: $$api.qInstances.getQInstanceFx,
});
sample({
    clock: $$docsGeneration.pollingFx.done,
    fn: () => undefined,
    target: $$api.participants.getMeFx,
});

// When any error happens, we navigate to the error page
sample({
    source: $$docsGeneration.$qInstance,
    clock: $$docsGeneration.pollingFx.fail,
    filter: (qInstance) => !!qInstance,
    fn: (qInstance) => {
        if (qInstance?.questionnaire_type_code === 'EP') {
            return `/document/EP/${qInstance?.state_code}/${qInstance?.questionnaire_instance_id}/continue`;
        }
        if (qInstance?.questionnaire_type_code === 'TRUST_FUND') {
            return '/dashboard/trust-funding';
        }
        if (qInstance?.questionnaire_type_code === 'GIFT_TRUST') {
            return '/dashboard/gift-trust';
        }
        return '/dashboard/complete';
    },
    target: navigateFx,
});
